/**
 * User Management API Service
 * Handles all user-related API calls
 */

import { ImportLeaveBalanceResponse, IUser, UserRole, UserStatus } from '@hrms/shared/types';

import apiClient from './interceptors';

export interface CreateUserData {
  empId: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  department?: string;
  designation?: string;
  phone?: string;
  reportingManager?: string;
  dottedLineManagers?: string[];
  hr?: string;
  dateOfJoining?: Date | null;
  managerGroupId?: string;
  newManagerGroupName?: string;
  altPhone?: string;
  offEmail?: string;
  location?: string;
  qualificationAndPassedYear?: string;
  degree?: string;
  presentAddress?: string;
  permanentAddress?: string;
  aadhar?: string;
  pan?: string;
  dob?: Date | null;
  bloodGroup?: string;
  emergencyContact?: {
    name?: string;
    relationship?: string;
    number?: string;
  };
  pastEmployer?: string;
  religion?: string;
  doe?: Date | null;
  remarks?: string;
  candidateId?: string;
}

export interface UpdateUserData {
  empId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  department?: string;
  designation?: string;
  phone?: string;
  reportingManager?: string | null;
  dottedLineManagers?: string[];
  hr?: string | null;
  dateOfJoining?: Date | null;
  status?: UserStatus;
  managerGroupId?: string;
  newManagerGroupName?: string;
  altPhone?: string;
  offEmail?: string;
  location?: string;
  qualificationAndPassedYear?: string;
  degree?: string;
  presentAddress?: string;
  permanentAddress?: string;
  aadhar?: string;
  pan?: string;
  dob?: Date | null;
  bloodGroup?: string;
  emergencyContact?: {
    name?: string;
    relationship?: string;
    number?: string;
  };
  pastEmployer?: string;
  religion?: string;
  doe?: Date | null;
  remarks?: string;
}

export interface UserFilters {
  role?: UserRole;
  department?: string;
  status?: UserStatus;
  search?: string;
  reportingManager?: string;
  hr?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  includeInactive?: boolean;
}

export interface UsersListResponse {
  users: IUser[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface UserStatsResponse {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  verifiedUsers: number;
  unverifiedUsers: number;
  usersByRole: Record<UserRole, number>;
  usersByStatus: Record<string, number>;
  departmentDistribution: Record<string, number>;
  recentRegistrations: number;
}

export interface UsersByRoleResponse {
  data: UsersListResponse;
  users: IUser[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface BulkImportData {
  users: CreateUserData[];
  sendWelcomeEmail?: boolean;
  skipDuplicates?: boolean;
}

export interface BulkImportResponse {
  imported: number;
  skipped: number;
  errors: Array<{ row: number; email: string; error: string }>;
}

export const usersApi = {
  // Get users list with filters
  getUsers: async (filters?: UserFilters): Promise<UsersListResponse> => {
    const response = await apiClient.get('/users', { params: filters });
    return response.data.data;
  },

  // Get user by ID
  getUserById: async (id: string): Promise<IUser> => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data.data.user;
  },

  // Create new user
  createUser: async (userData: CreateUserData): Promise<IUser> => {
    const response = await apiClient.post('/users', userData);
    return response.data.data;
  },

  // Update user
  updateUser: async (id: string, userData: UpdateUserData): Promise<IUser> => {
    const response = await apiClient.put(`/users/${id}`, userData);
    return response.data.data;
  },

  // Delete user
  deleteUser: async (id: string): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  // Search users
  searchUsers: async (query: string): Promise<IUser[]> => {
    const response = await apiClient.get('/users/search', { params: { q: query } });
    return response.data.data;
  },

  // Get user statistics
  getUserStats: async (): Promise<UserStatsResponse> => {
    const response = await apiClient.get('/users/stats');
    return response.data.data;
  },

  // Check email availability
  checkEmailAvailability: async (email: string): Promise<boolean> => {
    const response = await apiClient.get('/users/check-email', { params: { email } });
    return response.data.data.available;
  },

  // Bulk import users
  bulkImportUsers: async (importData: BulkImportData): Promise<BulkImportResponse> => {
    const response = await apiClient.post('/users/bulk-import', importData);
    return response.data.data;
  },

  // Get users by role
  getUsersByRole: async (role: UserRole): Promise<UsersByRoleResponse> => {
    const response = await apiClient.get(`/users/role/${role}`);
    return response.data;
  },

  // Get users by department
  getUsersByDepartment: async (department: string): Promise<IUser[]> => {
    const response = await apiClient.get(`/users/department/${department}`);
    return response.data.data;
  },

  // Get direct reports for a manager
  getDirectReports: async (managerId: string): Promise<IUser[]> => {
    const response = await apiClient.get(`/users/manager/${managerId}/reports`);
    return response.data.data;
  },

  // Change user password (admin only)
  changeUserPassword: async (id: string, newPassword: string): Promise<void> => {
    await apiClient.post(`/users/${id}/change-password`, { newPassword });
  },

  // Reset user login attempts
  resetLoginAttempts: async (id: string): Promise<void> => {
    await apiClient.post(`/users/${id}/reset-login-attempts`);
  },

  // Toggle user status
  toggleUserStatus: async (id: string): Promise<IUser> => {
    const response = await apiClient.post(`/users/${id}/toggle-status`);
    return response.data.data;
  },

  // Resend verification email
  resendVerificationEmail: async (id: string): Promise<void> => {
    await apiClient.post(`/users/${id}/resend-verification`);
  },

  // Mark email as verified directly (admin bypass)
  markEmailVerified: async (id: string): Promise<void> => {
    await apiClient.post(`/users/${id}/mark-verified`);
  },

  // Get verification link without sending email
  getVerificationLink: async (id: string): Promise<string> => {
    const response = await apiClient.get<{ success: boolean; data: { link: string } }>(`/users/${id}/verification-link`);
    return response.data.data.link;
  },
  async bulkUserforward(formData: FormData): Promise<ImportLeaveBalanceResponse> {
      const response = await apiClient.post<ImportLeaveBalanceResponse>(
        '/users/bulk-import-excel',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
  
      return response.data;
    },

  // Department management
  getDepartments: async (): Promise<string[]> => {
    const response = await apiClient.get<{ success: boolean; data: string[] }>('/users/departments');
    return response.data.data;
  },

  createDepartment: async (name: string): Promise<string> => {
    const response = await apiClient.post<{ success: boolean; data: string }>('/users/departments', { name });
    return response.data.data;
  },

  updateDepartment: async (oldName: string, newName: string): Promise<string> => {
    const response = await apiClient.put<{ success: boolean; data: string }>(
      `/users/departments/${encodeURIComponent(oldName)}`,
      { newName }
    );
    return response.data.data;
  },

  deleteDepartment: async (name: string): Promise<void> => {
    await apiClient.delete(`/users/departments/${encodeURIComponent(name)}`);
  },

  // Role management
  getRoles: async (): Promise<RoleItem[]> => {
    const response = await apiClient.get<{ success: boolean; data: RoleItem[] }>('/users/roles');
    return response.data.data;
  },

  createRole: async (name: string, category: 'executive' | 'standard' = 'executive'): Promise<RoleItem[]> => {
    const response = await apiClient.post<{ success: boolean; data: RoleItem[] }>('/users/roles', { name, category });
    return response.data.data;
  },

  updateRole: async (value: string, label: string, category?: 'executive' | 'standard'): Promise<RoleItem[]> => {
    const response = await apiClient.put<{ success: boolean; data: RoleItem[] }>(
      `/users/roles/${encodeURIComponent(value)}`,
      { label, category }
    );
    return response.data.data;
  },

  deleteRole: async (value: string): Promise<RoleItem[]> => {
    const response = await apiClient.delete<{ success: boolean; data: RoleItem[] }>(
      `/users/roles/${encodeURIComponent(value)}`
    );
    return response.data.data;
  },
};

export interface RoleItem {
  value: string;
  label: string;
  category: 'executive' | 'standard';
  isDefault?: boolean;
}
